import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import Login from "./Authorization/Login";
import Register from "./Authorization/Register";
import ProtectedRoute from "./Authorization/ProtectedRoute";
import AdminRoute from "./Authorization/AdminRoute";
import Home from "./Main/Home";
import Navbar from "./Main/Navbar/Navbar";
import DatabaseAdmin from "./DatabaseAdmin/DatabaseAdmin";
import ParsingPage from "./Parsing_Pages/ParsingPage";
import SourceData from "./Source_Data/Source_Data";
import ExportData from "./Export_Data/Export_Data";
import { getAPIUrl } from "./api/apiUtils";
import Admin from "./pages/Admin";
import PostsSearch from "./pages/PostsSearch";
import TaskNotifications from "./components/TaskNotifications";
import { useTaskNotifications } from "./hooks/useTaskNotifications";
import { apiFetch, AUTH_EXPIRED_EVENT } from "./api/apiClient";

const ALL_PAGES = [
  "Collect",
  "SourceData",
  "ExportData",
  "PostsSearch",
  "Admin",
];

const PAGE_PATHS = {
  Collect: "/collect",
  SourceData: "/source-data",
  ExportData: "/export-data",
  PostsSearch: "/posts-search",
  Admin: "/admin",
};

const ADMIN_ONLY_PAGES = new Set(["Collect", "Admin"]);

const VirtualPageTracker = () => {
  const location = useLocation();
  const previousUrlRef = useRef(window.location.href);

  useLayoutEffect(() => {
    const currentUrl = window.location.href;
    const previousUrl = previousUrlRef.current;

    if (currentUrl === previousUrl) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "updatevirtualpath",
      virtualPageURL: currentUrl,
      virtualPageTitle: document.title,
      virtualPageReferrer: previousUrl,
    });

    previousUrlRef.current = currentUrl;
  }, [location.pathname, location.search, location.hash]);

  return null;
};

const getFallbackPath = (pages, user) => {
  const page = pages.find((item) => user?.role === "admin" || !ADMIN_ONLY_PAGES.has(item));
  return PAGE_PATHS[page] || "/login";
};

const SessionExpiredDialog = ({ isOpen, onConfirm }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    navigate("/login", { replace: true });
  };

  return (
    <div className="session-expired-overlay" role="dialog" aria-modal="true" aria-labelledby="session-expired-title">
      <div className="session-expired-modal">
        <h2 id="session-expired-title">Нужно войти заново</h2>
        <p>Сессия истекла или токен больше не действителен. Авторизуйтесь снова, чтобы продолжить работу.</p>
        <button type="button" className="btn btn-primary session-expired-modal__button" onClick={handleConfirm} autoFocus>
          Перейти ко входу
        </button>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [allowedPages, setAllowedPages] = useState(ALL_PAGES);
  const [accessReady, setAccessReady] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const {
    notifications,
    unreadCount,
    isLoading: notificationsLoading,
    pollError: notificationsError,
    markSeen,
    markRead,
    seenIds,
  } = useTaskNotifications(user);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser);
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("deniedPages");
      setUser(null);
      setIsSessionExpired(true);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, []);

  const handleSessionExpiredConfirm = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("deniedPages");
    setUser(null);
    setIsSessionExpired(false);
  };

  useEffect(() => {
    if (user && user.id) {
      setAccessReady(false);
      apiFetch(`${getAPIUrl()}/users/user_access/${user.id}`)
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to load user access: ${res.status}`);
          return res.json();
        })
        .then((data) => {
          const deniedPages = data.filter((access) => access.can_view).map((access) => access.page);
          setAllowedPages(ALL_PAGES.filter((page) => !deniedPages.includes(page)));
        })
        .catch(() => setAllowedPages(ALL_PAGES))
        .finally(() => setAccessReady(true));
    } else {
      setAllowedPages(ALL_PAGES);
      setAccessReady(true);
    }
  }, [user]);

  const fallbackPath = getFallbackPath(allowedPages, user);

  return (
    <Router>
      <VirtualPageTracker />
      <div className="App app-shell">
        {user && (
          <Navbar
            user={user}
            setUser={setUser}
            allowedPages={allowedPages}
            notifications={notifications}
            unreadCount={unreadCount}
            notificationsLoading={notificationsLoading}
            notificationsError={notificationsError}
            markNotificationsSeen={markRead}
          />
        )}
        {user && (
          <TaskNotifications
            user={user}
            notifications={notifications}
            isLoading={notificationsLoading}
            pollError={notificationsError}
            markNotificationsSeen={markSeen}
            seenIds={seenIds}
          />
        )}
        <SessionExpiredDialog isOpen={isSessionExpired} onConfirm={handleSessionExpiredConfirm} />
        <div className="container mt-2 w-100">
          <Routes>
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register setUser={setUser} />} />
            <Route
              path="/"
              element={
                <AdminRoute page="Collect" allowedPages={allowedPages} accessReady={accessReady} fallbackPath={fallbackPath}>
                  <ParsingPage />
                </AdminRoute>
              }
            />
            <Route
              path="/collect"
              element={
                <AdminRoute page="Collect" allowedPages={allowedPages} accessReady={accessReady} fallbackPath={fallbackPath}>
                  <ParsingPage />
                </AdminRoute>
              }
            />
            <Route
              path="/source-data"
              element={
                <ProtectedRoute page="SourceData" allowedPages={allowedPages} accessReady={accessReady} fallbackPath={fallbackPath}>
                  <SourceData />
                </ProtectedRoute>
              }
            />
            <Route
              path="/export-data"
              element={
                <ProtectedRoute page="ExportData" allowedPages={allowedPages} accessReady={accessReady} fallbackPath={fallbackPath}>
                  <ExportData />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stat"
              element={
                <ProtectedRoute>
                  <Home user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/DatabaseAdmin"
              element={
                <AdminRoute page="Admin" allowedPages={allowedPages} accessReady={accessReady} fallbackPath={fallbackPath}>
                  <DatabaseAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute page="Admin" allowedPages={allowedPages} accessReady={accessReady} fallbackPath={fallbackPath}>
                  <Admin />
                </AdminRoute>
              }
            />
            <Route
              path="/posts-search"
              element={
                <ProtectedRoute page="PostsSearch" allowedPages={allowedPages} accessReady={accessReady} fallbackPath={fallbackPath}>
                  <PostsSearch />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
